import { z } from '@/utils/zod'

//
// Language
//

export const availableLanguages = ['en', 'de'] as const

export const LanguageSchema = z.enum(availableLanguages)

export type Language = z.infer<typeof LanguageSchema>

export const isAvailableLanguage = (language: string): language is Language =>
    (availableLanguages as unknown as string[]).includes(language)

//
// Country
//

export const availableCountries = ['DE', 'AT', 'US'] as const

export const CountrySchema = z.enum(availableCountries)

export type Country = z.infer<typeof CountrySchema>

export const isAvailableCountry = (country: string): country is Country =>
    (availableCountries as unknown as string[]).includes(country)

//
// Locale
//

export type Locale = `${Language}-${Country}`

export const LocaleSchema = z.string().check(({ value, ...ctx }) => {
    if (!isAvailableLocale(value)) {
        ctx.issues.push({
            code: 'custom',
            input: value,
        })
    }
}) as z.ZodType<Locale>

const defaultLocalesForLanguage = {
    en: 'en-US',
    de: 'de-DE',
} as const satisfies Record<Language, Locale>

const defaultLocalesForCountry: Partial<Record<Country, Locale>> = {
    DE: 'de-DE',
    AT: 'de-AT',
    US: 'en-US',
}

export const isAvailableLocale = (locale: string): locale is Locale => {
    const [language, country] = locale.split('-')
    return isAvailableLanguage(language) && isAvailableCountry(country)
}

export const getDefaultLocale = (languageOrCountry: Language | Country): Locale => {
    if (isAvailableLanguage(languageOrCountry)) {
        return defaultLocalesForLanguage[languageOrCountry]
    }

    return defaultLocalesForCountry[languageOrCountry] || `en-${languageOrCountry}`
}

export const getDefaultLanguage = (country: Country) => {
    const locale = defaultLocalesForCountry[country] || `en-${country}`
    return locale.split('-').shift() as Language
}

export const getLanguageFromLocale = (locale: Locale) => {
    return locale.split('-').shift() as Language
}

export const getCountryFromLocale = (locale: Locale) => {
    return locale.split('-').pop() as Country
}
